// public/js/features/tracker-config/battery-remaining-chart.js
// One "Battery remaining" card per paired tracker inside the asset page's
// Device health block: the capacity actually used so far as a solid line, and
// a dashed projection from today to end of life at the current rate, with the
// date it lands on. docs/battery-remaining.md.
//
// The curve is honest about what it knows. The daily map on the device row
// covers the last ~40 days, so that stretch is drawn day by day; everything
// before it is one straight segment at the lifetime average - which is exactly
// what the accounting has for that period.
import { estimateRemaining } from './battery-usage.js';
import { batteryHelpHtml } from './battery-remaining-ui.js';
import { featureEnabled } from '../../modules/features.js';
import { initHelp } from '../../ui/help.js';
import { escapeHtml } from '../../utils/escape.js';

const MS_PER_DAY = 86400000;

/** The two series for one device, in remaining-% over time. */
function seriesFor(device, est, now) {
  const usable = est.usableMAh;
  const pct = (consumed) => Math.max(0, Math.min(100, 100 - consumed / usable * 100));
  const epochMs = Date.parse(est.epochAt);

  // Daily buckets ascending, only those inside the accounting window.
  const daily = Object.entries(device.battDaily || {})
    .filter(([, v]) => Number(v) > 0)
    .map(([day, v]) => [Date.parse(`${day}T12:00:00Z`), Number(v)])
    .filter(([ms]) => ms >= epochMs)
    .sort((a, b) => a[0] - b[0]);
  const inWindow = daily.reduce((s, [, v]) => s + v, 0);

  const actual = [{ x: epochMs, y: 100 }];
  let running = Math.max(0, est.consumedMAh - inWindow);   // charge used before the window
  if (daily.length) {
    const windowStart = Math.max(epochMs, daily[0][0] - MS_PER_DAY);
    if (windowStart > epochMs) actual.push({ x: windowStart, y: pct(running) });
    for (const [ms, v] of daily) { running += v; actual.push({ x: ms, y: pct(running) }); }
  }
  actual.push({ x: now, y: pct(est.consumedMAh) });

  let projected = [];
  let eolMs = null;
  if (est.method !== 'externalPower' && isFinite(est.estRemainingDays) && est.estRemainingDays > 0) {
    eolMs = now + est.estRemainingDays * MS_PER_DAY;
    projected = [{ x: now, y: pct(est.consumedMAh) }, { x: eolMs, y: 0 }];
  }
  return { actual, projected, eolMs };
}

function fmtDate(ms, withDay = true) {
  return new Date(ms).toLocaleDateString(navigator.language,
    withDay ? { year: 'numeric', month: 'short', day: '2-digit' } : { year: 'numeric', month: 'short' });
}

function makeChart(canvas, s, est) {
  const spanDays = ((s.eolMs || Date.now()) - s.actual[0].x) / MS_PER_DAY;
  return new Chart(canvas, {
    type: 'line',
    data: {
      datasets: [
        {
          label: 'Used', data: s.actual, borderColor: '#2563eb', backgroundColor: 'rgba(37,99,235,0.08)',
          borderWidth: 2, pointRadius: 0, fill: true, tension: 0, parsing: false,
        },
        ...(s.projected.length ? [{
          label: 'Projected', data: s.projected, borderColor: '#94a3b8', borderDash: [6, 4],
          borderWidth: 2, pointRadius: [0, 4], pointBackgroundColor: '#ef4444', fill: false, tension: 0, parsing: false,
        }] : []),
      ],
    },
    options: {
      responsive: true, maintainAspectRatio: false, animation: false,
      interaction: { mode: 'nearest', intersect: false },
      plugins: {
        legend: { display: true, position: 'bottom', labels: { boxWidth: 10, font: { size: 10 } } },
        tooltip: {
          callbacks: {
            title: (items) => fmtDate(items[0].parsed.x),
            label: (item) => `${item.dataset.label}: ${item.parsed.y.toFixed(1)} % remaining`,
          },
        },
      },
      scales: {
        x: {
          type: 'linear', min: s.actual[0].x, max: s.eolMs || Date.now(),
          ticks: { maxTicksLimit: 6, font: { size: 10 }, callback: (v) => fmtDate(v, spanDays < 120) },
          grid: { display: false },
        },
        y: {
          min: 0, max: 100,
          ticks: { maxTicksLimit: 5, font: { size: 10 }, callback: (v) => `${v} %` },
          grid: { color: 'rgba(0,0,0,0.05)' },
        },
      },
    },
  });
}

/**
 * Append one card per tracker with accounting into the Device health grid of
 * `sectionEl`. Creates the collapsed block when the sensor charts produced none
 * (an asset whose only telemetry is positions). Returns the Chart instances so
 * the caller can destroy them on redraw.
 */
export function renderBatteryRemainingCards(sectionEl, devices = [], labels = null) {
  if (!featureEnabled('batteryEstimate') || !sectionEl || typeof Chart === 'undefined') return [];
  const now = Date.now();
  const items = devices
    .filter(d => !!d)
    .map(d => ({ d, est: estimateRemaining(d, now) }))
    .filter(x => x.est);
  if (!items.length) return [];

  let grid = sectionEl.querySelector('#ssr-health-collapse .sensor-chart-grid');
  if (!grid) {
    sectionEl.insertAdjacentHTML('beforeend', `
      <button type="button" class="ssr-health-toggle collapsed text-muted small mt-3 mb-1 d-flex align-items-center gap-2 w-100"
        data-bs-toggle="collapse" data-bs-target="#ssr-health-collapse" aria-expanded="false" aria-controls="ssr-health-collapse">
        <i class="fa-solid fa-chevron-right ssr-health-chevron"></i> Device health <span class="text-muted ssr-health-count">(0)</span>
      </button>
      <div class="collapse" id="ssr-health-collapse"><div class="sensor-chart-grid"></div></div>`);
    grid = sectionEl.querySelector('#ssr-health-collapse .sensor-chart-grid');
  }

  const charts = [];
  const help = {};
  items.forEach(({ d, est }, i) => {
    help[`battery.chart.${d.deviceId}`] = batteryHelpHtml(est);
    const s = seriesFor(d, est, now);
    const name = (labels && labels[d.deviceId]) || d.deviceId;
    const eol = s.eolMs
      ? `End of life ~ ${fmtDate(s.eolMs)}`
      : (est.method === 'externalPower' ? 'External power' : 'No projection yet');
    const id = `battery-remaining-canvas-${i}`;
    grid.insertAdjacentHTML('beforeend', `
      <div class="sensor-card battery-remaining-card">
        <div class="ssr-card-head">
          <span class="ssr-card-title">Battery remaining <span class="text-muted">- ${escapeHtml(name)}</span></span>
          <span class="ssr-card-stats">${escapeHtml(eol)}${est.confidence === 'low' ? ` <i class="fa-regular fa-circle-question" data-help="battery.chart.${escapeHtml(d.deviceId)}" tabindex="0"></i>` : ''}</span>
        </div>
        <div class="ssr-card-canvas"><canvas id="${id}"></canvas></div>
      </div>`);
    const canvas = grid.querySelector(`#${id}`);
    if (canvas) charts.push(makeChart(canvas, s, est));
  });

  // Standard tooltips (ui/help.js) on the confidence marks - the same look as
  // every other "?" in the app, not the browser's native title.
  initHelp(grid, help);

  // Keep the block's count honest.
  const toggle = sectionEl.querySelector('.ssr-health-toggle');
  if (toggle) {
    const n = grid.querySelectorAll('.sensor-card').length;
    const cnt = toggle.querySelector('.text-muted');
    if (cnt) cnt.textContent = `(${n})`;
  }
  return charts;
}
